import ErrorComponent from './components/Error';
import TelemedicineContext from './context/telemedicine';
import { get } from './services/telemedicine';
import { LIVEKIT_PROVIDER } from './utils/telemedicine';
import { getToken, saveToken } from './utils/utils';

import { Flex, Loading, ModalProvider } from '@amigoapp/doca-react';
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import io from 'socket.io-client';

function Root() {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [telemedicine, setTelemedicine] = useState();
	const [searchParams, setSearchParams] = useSearchParams();

	const handleRedirect = useCallback(telemedicine => {
		if (telemedicine?.status === 'done') {
			return navigate('/telemedicine-finished');
		}

		if (telemedicine?.provider === LIVEKIT_PROVIDER) {
			return navigate('/waiting-room');
		}

		if (!telemedicine?.signed_at) {
			return navigate('/home');
		}

		if (telemedicine?.signed_at) {
			return navigate('/waiting-room');
		}

		return null;
	}, [navigate]);

	const handleUpdateTelemedicine = useCallback(newData => {
		setTelemedicine(telemedicine => ({
			...telemedicine,
			...newData
		}));
	}, []);

	const handleFetchTelemedicine = useCallback(async () => {
		const { data } = await get();

		setTelemedicine(data);
		setLoading(false);

		handleRedirect(data);
	}, [handleRedirect]);

	const handleConnectSocket = useCallback(() => {
		const token = searchParams.get('token');

		if (token) {
			saveToken(token);

			searchParams.delete('token');
			setSearchParams(searchParams);
		}

		if (!window.socket) {
			window.socket = io(process.env.REACT_APP_TELEMEDICINE_SOCKET_URL, {
				reconnection: true,
				reconnectionDelay: 6000,
				reconnectionDelayMax: 6000,
				query: { token: getToken() }
			});

			window.socket.on('telemedicine:refresh-new-room', data => {
				saveToken(data.token);
				window.location.reload();
			});
		}
	}, [searchParams, setSearchParams]);

	useEffect(() => {
		handleConnectSocket();
		handleFetchTelemedicine();
	}, [handleConnectSocket, handleFetchTelemedicine]);

	/**
	 * Re-enter the socket room whenever the socket comes back.
	 *
	 * This lives here rather than in the waiting room because the patient stays in the
	 * room for the whole consultation: a reconnect that does not re-enter drops them from
	 * every room-scoped event — 'telemedicine:room-closed', document delivery — for the
	 * rest of the call, with nothing on screen to say so. Root is the only component the
	 * router keeps mounted across all of it.
	 */
	useEffect(() => {
		if (!telemedicine?.id) {
			return undefined;
		}

		const handleReconnect = () => {
			window.socket.emit('telemedicine:enter-room', {
				room_id: telemedicine.id,
				patient_id: telemedicine.patient_id
			});
		};

		window.socket.on('reconnect', handleReconnect);

		return () => {
			window.socket.off('reconnect', handleReconnect);
		};
	}, [telemedicine?.id, telemedicine?.patient_id]);

	if (!loading && !telemedicine) {
		return <ErrorComponent />;
	}

	return (
		<TelemedicineContext.Provider value={{ telemedicine, updateTelemedicine: handleUpdateTelemedicine }}>
			<ModalProvider>
				{loading
					? <Loading size="large" />
					: (
						<Flex className="app-shell" flex={1}>
							<Outlet />
						</Flex>
					)
				}
			</ModalProvider>
		</TelemedicineContext.Provider>
	);
}

export default Root;
